"use client";

import { createContext, useCallback, useContext, useState } from "react";
import { errorText, useT } from "./i18n";

type Kind = "ok" | "error";
type Item = { id: number; kind: Kind; text: string };
type Ctx = { ok: (text: string) => void; fail: (e: unknown) => void };

const ToastContext = createContext<Ctx>({ ok: () => {}, fail: () => {} });
let nextId = 1;

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const t = useT();
  const [items, setItems] = useState<Item[]>([]);

  const push = useCallback((kind: Kind, text: string) => {
    const id = nextId++;
    setItems((xs) => [...xs, { id, kind, text }]);
    setTimeout(() => setItems((xs) => xs.filter((x) => x.id !== id)), kind === "error" ? 8000 : 3500);
  }, []);

  const value: Ctx = {
    ok: (text) => push("ok", text),
    fail: (e) => push("error", errorText(t, e)),
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="toasts" role="status" aria-live="polite">
        {items.map((i) => (
          <div key={i.id} className={`toast toast-${i.kind}`}>
            {i.text}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export const useToast = () => useContext(ToastContext);
